'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useCustomAddressStore } from '@libs/common-next';
import { type ErrorMessageType } from './error.types';
import { EMPTY_FORM_VALUES, FORM_FIELD_OPTIONS, REQUIRED_FIELDS } from './form.constants';
import { getFormSchema } from './form.schema';
import { type AddressSelectData, type FormFields } from './form.types';
export type UseSddAddressFormProps = {
  onSuccessAction?: () => void;
  isDeliveryAvailable: boolean;
  isLoading: boolean;
  isError: boolean;
};
export const useSddAddressForm = ({
  onSuccessAction,
  isDeliveryAvailable,
  isLoading,
  isError
}: UseSddAddressFormProps) => {
  const [errorType, setErrorType] = useState<ErrorMessageType | null>(null);
  const [showComplement, setShowComplement] = useState(false);
  const [isWaitingForResult, setIsWaitingForResult] = useState(false);
  const sddAddress = useCustomAddressStore(state => state.address);
  const setSddAddress = useCustomAddressStore(state_0 => state_0.setAddress);
  const formSchema = useMemo(() => getFormSchema(), []);
  const form = useForm<FormFields>({
    defaultValues: {
      streetLine1: sddAddress?.streetLine1 ?? '',
      streetLine2: sddAddress?.streetLine2,
      city: sddAddress?.city ?? '',
      stateProvince: sddAddress?.stateProvince ?? '',
      zipPostalCode: sddAddress?.zipPostalCode ?? ''
    },
    resolver: zodResolver(formSchema),
    mode: 'onSubmit'
  });
  const {
    setValue,
    trigger,
    clearErrors,
    handleSubmit,
    watch
  } = form;
  const onSubmit = async (values: FormFields) => {
    setErrorType(null);
    setIsWaitingForResult(true);
    setSddAddress(values);
  };
  const onInvalid = useCallback(() => {
    const formValues = form.getValues();
    const isAddressEmpty = REQUIRED_FIELDS.some(field => !formValues[field]?.trim());
    setErrorType(isAddressEmpty ? 'emptyFields' : 'invalidFields');
  }, [form]);
  const handleFormReset = useCallback(() => {
    clearErrors();
    setErrorType(null);
    setShowComplement(false);
    setIsWaitingForResult(false);
    form.reset(EMPTY_FORM_VALUES);
  }, [clearErrors, form]);
  const handleFormSubmit = () => {
    handleSubmit(onSubmit, onInvalid)();
  };
  const handleAddressSelect = useCallback((addressData: AddressSelectData) => {
    setValue('streetLine1', addressData.street, FORM_FIELD_OPTIONS);
    setValue('city', addressData.city, FORM_FIELD_OPTIONS);
    setValue('stateProvince', addressData.state, FORM_FIELD_OPTIONS);
    setValue('zipPostalCode', addressData.zip, FORM_FIELD_OPTIONS);
    trigger(REQUIRED_FIELDS);
  }, [setValue, trigger]);
  const complementValue = watch('streetLine2');
  const toggleComplement = useCallback(() => {
    setShowComplement(prev => !prev);
    if (showComplement) {
      setValue('streetLine2', '', {
        shouldDirty: true
      });
    }
  }, [showComplement, setValue]);
  useEffect(() => {
    if (complementValue && !showComplement) {
      setShowComplement(true);
    }
  }, [complementValue, showComplement]);
  useEffect(() => {
    if (errorType === 'apiError' || errorType === 'estimationValidation') return;
    if (form.formState.isValid) {
      setErrorType(null);
    }
  }, [errorType, form.formState.isValid]);
  useEffect(() => {
    if (!isWaitingForResult || isLoading) return;
    setIsWaitingForResult(false);
    if (isError) {
      setErrorType('apiError');
      return;
    }
    if (!isDeliveryAvailable) {
      setErrorType('estimationValidation');
      return;
    }
    onSuccessAction?.();
  }, [isWaitingForResult, isLoading, isError, isDeliveryAvailable, onSuccessAction]);
  return {
    ...form,
    onSubmit: handleFormSubmit,
    handleAddressSelect,
    handleFormReset,
    isValidating: isWaitingForResult && isLoading,
    errorType,
    showComplement,
    toggleComplement
  };
};